// @flow
import React, { Component } from 'react';
import { isMobileBrowser } from '../../../base/environment/utils';
import { translate } from '../../../base/i18n';
import { IconArrowUp } from '../../../base/icons';
import JitsiMeetJS from '../../../base/lib-jitsi-meet/_';
import { MEDIA_TYPE } from '../../../base/media';
import { connect } from '../../../base/redux';
import { ToolboxButtonWithIcon } from '../../../base/toolbox/components';
import { FEATURE_CUSTOM_REDUX } from '../../../customise/constant';
import { GenericConfirmationModal } from '../../../customise/custom/components/GenericConfirmationModal';
import { dispatch } from '../../../customise/Utils';
import { AudioSettingsPopup, toggleAudioSettings } from '../../../settings';
import { getAudioSettingsVisibility } from '../../../settings/functions';
import { muteLocal } from '../../../video-menu/actions.any';
import { Translate } from '../../../webjam/language';
import { isAudioSettingsButtonDisabled } from '../../functions';
import AudioMuteButton from '../AudioMuteButton';
// CUSTOM
import { isPrejoinPageVisible } from '../../../prejoin/functions';
import { ct } from '../../../customise/constant-Utils';
// END

type Props = {

    /**
     * The button's key.
     */
    buttonKey?: string,

    /**
     * External handler for click action.
     */
    handleClick: Function,

    /**
     * Indicates whether audio permissions have been granted or denied.
     */
    hasPermissions: boolean,

    /**
     * Click handler for the small icon. Opens audio options.
     */
    onAudioOptionsClick: Function,

    /**
     * If the button should be disabled.
     */
    isDisabled: boolean,

    /**
     * Notify mode for `toolbarButtonClicked` event -
     * whether to only notify or to also prevent button click routine.
     */
    notifyMode?: string,

    /**
     * Used for translation.
     */
    t: Function,

    /**
     * Flag controlling the visibility of the button.
     * AudioSettings popup is disabled on mobile browsers.
     */
    visible: boolean,

    /**
     * Defines is popup is open.
     */
    isOpen: boolean,
};

/**
 * Button used for audio & audio settings.
 *
 * @returns {ReactElement}
 */
class AudioSettingsButton extends Component<Props> {
    /**
     * Initializes a new {@code AudioSettingsButton} instance.
     *
     * @inheritdoc
     */
    constructor(props: Props) {
        super(props);
        //CUSTOM
        this.state = {
            openConfirmationModal: false,
            isMicMutedOnce: false, // if user unmute mic once, then we set isMicMuteOnce = true, so we detect it already unmuted, in that case we no need to show Modal	
            handleMic: null
        }
        //END
        this._onEscClick = this._onEscClick.bind(this);
        this._onClick = this._onClick.bind(this);
    }

    _onEscClick: (KeyboardEvent) => void;

    /**
     * Click handler for the more actions entries.
     *
     * @param {KeyboardEvent} event - Esc key click to close the popup.
     * @returns {void}
     */
    _onEscClick(event) {
        if (event.key === 'Escape' && this.props.isOpen) {
            event.preventDefault();
            event.stopPropagation();
            this._onClick();
        }
    }

    _onClick: () => void;

    /**
     * Click handler for the more actions entries.
     *
     * @returns {void}
     */
    _onClick() {
        const { onAudioOptionsClick } = this.props;

        onAudioOptionsClick();
    }
    //CUSTOM
    onShowModal() {
        this.setState({ openConfirmationModal: true, });
    }
    onSuccess() {
        dispatch(muteLocal(false, MEDIA_TYPE.AUDIO));
        this.setState({ openConfirmationModal: false, isMicMutedOnce: true });
    }
    //END
    /**
     * Implements React's {@link Component#render}.
     *
     * @inheritdoc
     */
    render() {
        const { hasPermissions, isDisabled, visible, isOpen, buttonKey, notifyMode, usersData, t, isPreJoinPageVisible } = this.props;
        //CUSTOM
        const settingsDisabled = !hasPermissions
            || isDisabled
            || !JitsiMeetJS.mediaDevices.isMultipleAudioInputSupported() || (!!usersData && isPreJoinPageVisible);
        return <>
            {(this.state.openConfirmationModal && !this.state.isMicMutedOnce) && <GenericConfirmationModal title={ct("rta-trmc.dialogs.notice")}
                content={ct("Translate.messages.micAccessDenied")}
                onSuccess={() => this.onSuccess()}
                onCancel={() => this.setState({ openConfirmationModal: false })}
                successBtnName={ct("Translate.buttonTitle.unmute")}
            />}
            {visible ? (
                <AudioSettingsPopup>
                    <ToolboxButtonWithIcon
                        ariaControls='audio-settings-dialog'
                        ariaExpanded={isOpen}
                        ariaHasPopup={true}
                        ariaLabel={t('toolbar.audioSettings')}
                        buttonKey={buttonKey}
                        icon={IconArrowUp}
                        iconDisabled={settingsDisabled}
                        iconId='audio-settings-button'
                        iconTooltip={t('toolbar.audioSettings')}
                        notifyMode={notifyMode}
                        onIconClick={this._onClick}
                        onIconKeyDown={this._onEscClick}>
                        <AudioMuteButton
                            buttonKey={buttonKey}
                            notifyMode={notifyMode}
                            isBoxConnected={!!usersData}
                            isMicMutedOnce={this.state.isMicMutedOnce}
                            handleShowModal={(status) => this.onShowModal(status)}
                        />
                    </ToolboxButtonWithIcon>
                </AudioSettingsPopup>
            ) : <AudioMuteButton
                buttonKey={buttonKey}
                notifyMode={notifyMode}
                isBoxConnected={!!usersData}
                isMicMutedOnce={this.state.isMicMutedOnce}
                handleShowModal={(status) => this.onShowModal(status)}
            />} </>;
        //END
    }
}

/**
 * Function that maps parts of Redux state tree into component props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
    const { permissions = {} } = state['features/base/devices'];
    const { isNarrowLayout } = state['features/base/responsive-ui'];
    //CUSTOM
    const usersData = state[FEATURE_CUSTOM_REDUX].usersData;
    const isPreJoinPageVisible = isPrejoinPageVisible(state);

    return {
        hasPermissions: permissions.audio,
        isDisabled: isAudioSettingsButtonDisabled(state),
        isOpen: getAudioSettingsVisibility(state),
        visible: !isMobileBrowser() && !isNarrowLayout,
        usersData,
        isPreJoinPageVisible
    };
    //END
}

const mapDispatchToProps = {
    onAudioOptionsClick: toggleAudioSettings
};

export default translate(connect(
    mapStateToProps,
    mapDispatchToProps
)(AudioSettingsButton));
